import { CartLinesUpdateEvent } from '@shopify/events';

class CompleteLook extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.data = JSON.parse(this.querySelector('[data-look-data]').textContent);
    this.form = this.querySelector('form');
    this.colour = this.querySelector('[data-look-colour]');
    this.selects = [...this.querySelectorAll('[data-look-size]')];
    this.button = this.querySelector('button[type="submit"]');
    this.label = this.querySelector('[data-look-button]');
    this.status = this.querySelector('[data-look-status]');
    this.bagLink = this.querySelector('[data-look-bag]');
    this.format = new Intl.NumberFormat(this.data.locale, {
      style: 'currency', currency: this.data.currency, minimumFractionDigits: 0, maximumFractionDigits: 2,
    });
    this.fractionFormat = new Intl.NumberFormat(this.data.locale, {
      style: 'currency', currency: this.data.currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
    this.main = document.querySelector(`heritage-product:has(product-form-component[data-product-id="${this.dataset.productId}"])`);
    this.colour.closest('.cl__colour').hidden = false;
    this.querySelector('.cl__total').hidden = false;
    this.syncFromMain();
    this.main?.addEventListener('change', event => {
      if (event.target.matches('[data-colour],[data-size]')) this.syncFromMain();
    }, { signal });
    window.addEventListener('popstate', () => this.syncFromMain(), { signal });
    this.colour.addEventListener('change', () => {
      const radio = [...(this.main?.querySelectorAll('[data-colour]') || [])].find(input => input.value === this.colour.value);
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      } else this.render();
    }, { signal });
    this.selects.forEach((select, index) => select.addEventListener('change', () => {
      this.clearFeedback();
      if (index === 0) {
        const variant = this.data.pieces[0].variants.find(v => v.id === select.value);
        const radio = [...(this.main?.querySelectorAll('[data-size]') || [])].find(input => input.value === variant?.size);
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this.updateTotal();
    }, { signal }));
    this.form.addEventListener('submit', event => this.addPair(event), { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
  }

  syncFromMain() {
    const colour = this.main?.querySelector('[data-colour]:checked')?.value;
    if (colour) this.colour.value = colour;
    this.render(this.main?.querySelector('[data-size]:checked')?.value || '');
  }

  render(teeSize) {
    this.clearFeedback();
    this.selects.forEach((select, index) => {
      const piece = this.data.pieces[index];
      const previous = piece.variants.find(v => v.id === select.value)?.size;
      const size = index === 0 && teeSize !== undefined ? teeSize : previous;
      const variants = piece.variants.filter(v => v.colour === this.colour.value);
      const placeholder = new Option(this.data.sizePlaceholder, '', true, true);
      placeholder.disabled = true;
      select.replaceChildren(placeholder, ...variants.map(variant => {
        const option = new Option(`${variant.size}${variant.available ? '' : ' - ' + this.data.soldOutLabel}`, variant.id);
        option.disabled = !variant.available;
        return option;
      }));
      const chosen = variants.find(v => v.size === size && v.available);
      if (chosen) select.value = chosen.id;
      const image = this.querySelector(`[data-look-piece="${index}"] img`);
      const src = piece.images[this.colour.value];
      if (src && new URL(image.src, location.origin).pathname !== new URL(src, location.origin).pathname) {
        image.srcset = [180, 360, 540, 720].map(width => {
          const url = new URL(src, location.origin);
          url.searchParams.set('width', width);
          return `${url.href} ${width}w`;
        }).join(', ');
        image.src = src;
      }
      image.alt = `${piece.title}, ${this.colour.value}`;
      select.disabled = this.pending || !variants.some(v => v.available);
    });
    this.updateTotal();
  }

  updateTotal() {
    let total = 0, discounted = 0, available = true;
    this.data.pieces.forEach((piece, index) => {
      const variants = piece.variants.filter(v => v.colour === this.colour.value);
      const variant = variants.find(v => v.id === this.selects[index].value) || variants.find(v => v.available) || variants[0];
      available = available && variants.some(v => v.available);
      const price = variant?.price || 0;
      total += price;
      discounted += Math.round(price * (100 - this.data.discount) / 100);
      this.querySelector(`[data-look-piece="${index}"] [data-piece-price]`).textContent = this.money(price);
    });
    this.button.disabled = this.pending || this.unconfirmed || !available;
    if (!available) this.label.textContent = this.data.soldOutLabel;
    const original = this.querySelector('[data-look-original]');
    original.hidden = !this.data.discount;
    original.textContent = this.money(total);
    this.querySelector('[data-look-total]').textContent = this.money(discounted);
  }

  money(cents) {
    return (cents % 100 ? this.fractionFormat : this.format).format(cents / 100);
  }

  clearFeedback() {
    if (this.pending || this.unconfirmed) return;
    this.button.dataset.state = 'idle';
    this.label.textContent = this.data.addLabel;
    this.status.hidden = true;
  }

  async addPair(event) {
    event.preventDefault();
    if (this.pending || this.unconfirmed || !this.form.reportValidity()) return;
    const items = this.selects.map(select => ({ id: select.value, quantity: 1 }));
    if (items.some((item, i) => !this.data.pieces[i].variants.some(v => v.id === item.id && v.available && v.colour === this.colour.value))) return;
    this.pending = true;
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    this.label.textContent = this.data.addingLabel;
    this.status.hidden = true;
    this.selects.forEach(select => { select.disabled = true; });
    this.colour.disabled = true;
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product', lines: items.map(item => ({ merchandiseId: item.id, quantity: 1 })), promise: deferred.promise,
    }));
    const root = this.data.root.replace(/\/?$/, '/');
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0, 5);
    let submitted = false;
    try {
      // One request adds the two independently selected sizes together.
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: location.pathname + location.search }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        const error = new Error(result.description || this.data.errorLabel);
        error.confirmed = true;
        throw error;
      }
      submitted = true;
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(this.data.uncertainLabel);
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: {
        items: cart.items, source: 'product-form-component', sourceId: this.id, itemCount: 2,
        productId: this.dataset.productId, sections: result.sections, didError: false,
      } });
      this.button.dataset.state = 'added';
      this.label.textContent = this.data.addedLabel;
      this.status.textContent = this.data.addedLabel;
      this.bagLink.hidden = false;
    } catch (error) {
      deferred.reject(error);
      // A lost response can still mean the add succeeded. Never automatically retry it.
      this.unconfirmed = submitted || !error.confirmed;
      this.label.textContent = this.data.addLabel;
      this.status.textContent = this.unconfirmed ? this.data.uncertainLabel : error.message;
      this.bagLink.hidden = false;
    } finally {
      this.pending = false;
      this.button.removeAttribute('aria-busy');
      this.status.hidden = false;
      this.colour.disabled = false;
      this.selects.forEach(select => { select.disabled = false; });
      this.updateTotal();
    }
  }
}

if (!customElements.get('complete-look')) customElements.define('complete-look', CompleteLook);
